import { useCallback, useMemo, useRef, useEffect, ComponentType, PropsWithRef } from 'react';
import { ESCAPE_KEY, UP_ARROW, DOWN_ARROW, END, HOME, TAB_KEY } from 'constants/keyCodes';

import * as Popover from '@radix-ui/react-popover';
import { useDropdownPopoverAlignment } from 'ui/utility/hooks/useDropdownPopoverAlignment';
import { HORIZONTAL_ALIGNMENT, VERTICAL_ALIGNMENT } from 'ui/utility/types';
import { ALIGNMENT, BASIC_SELECT_SIZES } from './constants';
import { VirtualizedOptionsList } from './VirtualizedOptionsList';
import { OptionsList } from './OptionsList';
import { BasicOption } from './BasicOption';
import { useAriaSelectProps, useCategorySelection, useSelectOpenTracking } from './hooks';
import {
  OptionWrapper,
  SelectContainer,
  SelectWrapper as StyledSelectWrapper,
  TriggerContainer,
  FooterButton,
  SelectOptionsContainer as PrimitiveSelectOptionsContainer,
} from './Selects.styles';
import { SelectValueDisplay } from './SelectValueDisplay';
import { SelectProps, SingleSelectProps, SelectValueDisplayProps, SelectValueType } from './types';
import { createDropdownToggle, createUniqueOptionsAndDefaults } from './utils';

export const SINGLE_SELECT_EMPTY_TEXT_TEST_ID = 'single-select-empty-text-test-id';
export const RAW_SELECT_DATA_PORTAL_ID = 'raw-select-portal';

/** this should be raw but there's too many files t
 * hat have it as "basic-select-trigger..." without actually using this function T___T as reference
 * */
export const getBasicSelectWrapperTestId = (id: string | number) =>
  `basic-select-trigger${id ? `-${id}` : ''}`;

export type RawSelectProps<TValue extends SelectValueType = string> = SelectProps<TValue> &
  SingleSelectProps;

/**
 *
 * Most of this logic is extracted from previous BasicSelect component and converted as RawSelect for better reusability
 */
export function RawSelect<TValue extends SelectValueType = string>({
  buttonStyles = {},
  categoryLabels = {},
  children,
  className,
  defaultValues = [], // setting this to a string will break option selection
  emptyStateText = 'No options available',
  automaticallyAvoidCollisions = false,
  horizontalAlignment = ALIGNMENT.LEFT,
  id = '',
  isDisabled = false,
  isError = false,
  isReadOnly = false,
  isRequired = false,
  labelId = '',
  onSelect = () => undefined,
  Option = BasicOption,
  options = [],
  placeholder = '',
  SelectWrapper = StyledSelectWrapper,
  SelectOptionsContainer = PrimitiveSelectOptionsContainer,
  shouldContainPopover = false,
  shouldFocusOnFirstElementByDefault = true,
  shouldOpenOnDefault = false,
  size = BASIC_SELECT_SIZES.MEDIUM,
  style = {},
  title = '',
  EmptyState,
  Trigger = SelectValueDisplay as ComponentType<
    React.PropsWithChildren<PropsWithRef<SelectValueDisplayProps<TValue>>>
  >,
  verticalAlignment = ALIGNMENT.DOWN,
  isVirtualized = false,
  canFetch = false,
  onFetch = () => undefined,
  suffixUsed = false,
  showAllCategories = false,
  footerButtonContent,
  filterText = '',
  isLoading = false,
  autoFocusOnInput = false,
  isFullWidth = false,
  portalElementRef,
  allowClickOutsideToClose = true,
  hideWhenDetached = true,
  disableInternalFiltering = false,
  showCategoryDivider = false,
  shouldDisableOnEmpty = true,
  ...props
}: RawSelectProps<TValue>) {
  const isExternalSearchUsed = disableInternalFiltering;
  /** Refs - used for focus tracking */
  const parentEl = useRef<HTMLDivElement>(null); // a ref for the wrapper
  const triggerContainerEl = useRef<HTMLDivElement>(null); // the trigger container for the select
  const triggerEl = useRef<HTMLButtonElement>(null); // the trigger for the select
  const optionsEl = useRef<HTMLDivElement & HTMLUListElement>(null); // the wrapper of the options

  /**
    Return the options and the defaultOptions as uniques, the memoization
    here is critical to control state thrashing in the useOptionsSelection
    hooks
    new optionId = parentId + "-" + optionId
    new defaultIds = parentId + "-" + defaultId
  */
  const [uniqueOptions, uniqueDefaults] = useMemo(
    () => createUniqueOptionsAndDefaults<TValue>(id, options, defaultValues),
    [id, options, defaultValues],
  );

  /** see documentation inside useCategorySelection.js */
  const { categoryDetail, getOptions, selectedOptions, singleSelect } = useCategorySelection({
    callback: onSelect,
    defaultSelections: uniqueDefaults,
    filterText,
    options: uniqueOptions,
    isExternalSearchUsed,
    showAllCategories,
  });

  /** Build out the associated ARIA props by the state of the component */
  const ariaProps = useAriaSelectProps({
    // if a single node is active, it becomes the activeId on the listbox
    activeId: selectedOptions.length === 1 ? selectedOptions[0].id : '',
    isDisabled,
    isReadOnly,
    isRequired,
    labelId,
    title,
  });
  // We also are going to block interactivity until we ensure that
  // options are present should protect against downstream errors
  // from waiting on async options loading
  //
  // The exception is when we are using external search, as empty options may
  // just mean that the value they're searching by yielded no results
  const isNonInteractive =
    isDisabled || isReadOnly || (!isExternalSearchUsed && !options.length && shouldDisableOnEmpty);

  /**
    This tracks the open/closed status for the select, and handles focus
    autoFocusOnInput: focuses on input regardless of selection
    no selection: focuses first item
    single selection: focuses that selection
    multiples selections: focuses first selected option
   */
  const [isOpen, setIsOpen] = useSelectOpenTracking({
    optionsEl,
    selectedOptions: selectedOptions.map((option) => `${option.uniqueId}`),
    triggerEl,
    shouldFocusOnFirstElementByDefault,
    autoFocusOnInput,
  });
  const toggleDropdown = createDropdownToggle(setIsOpen, isOpen);

  useEffect(() => {
    if (shouldOpenOnDefault) {
      toggleDropdown(true);
    }
  }, []);

  // this cb executes a fn if any of the listed keys match the keycode
  // documentation on required ARIA keyboard interactions here
  // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/listbox_role
  const handleKeyPress = useCallback((e: any) => {
    const { keyCode } = e;

    try {
      const focusableItems = Array.from(
        optionsEl.current?.querySelectorAll('input[type="text"], [role="option"]') || [],
      ).map((child) => ({
        el: child as HTMLElement,
        id: child.getAttribute('id'),
        isFocused: child === document.activeElement,
      }));

      const focusedOptionIndex = focusableItems.findIndex((item) => item.isFocused);
      const [firstOption, lastOption] = [
        focusableItems[0],
        focusableItems[focusableItems.length - 1],
      ];

      const isItemFocused =
        Boolean(document.activeElement) &&
        focusableItems.map((item) => item.el).includes(document.activeElement as HTMLElement);
      const isFirstItemFocused = firstOption?.el === document.activeElement;
      const isLastItemFocused = lastOption?.el === document.activeElement;
      const isShiftKeyPressed = e.shiftKey;

      switch (keyCode) {
        case END: {
          lastOption?.el?.focus();
          break;
        }

        case HOME: {
          firstOption?.el?.focus();
          break;
        }

        case ESCAPE_KEY: {
          toggleDropdown(false);
          break;
        }

        case TAB_KEY: {
          if (isItemFocused && isLastItemFocused && !isShiftKeyPressed) {
            e.preventDefault(); // interrupt the tab key
            firstOption?.el?.focus(); // cycle back to the first element
          }

          if (isItemFocused && isFirstItemFocused && isShiftKeyPressed) {
            e.preventDefault(); // interrupt the tab key
            lastOption?.el?.focus(); // cycle back to the last element
          }
          break;
        }

        case UP_ARROW: {
          if (isItemFocused && !isFirstItemFocused) {
            focusableItems[focusedOptionIndex - 1]?.el?.focus();
          } else {
            lastOption?.el?.focus();
          }
          break;
        }

        case DOWN_ARROW: {
          if (!isItemFocused || isLastItemFocused) {
            firstOption?.el?.focus();
          } else {
            focusableItems[focusedOptionIndex + 1]?.el?.focus();
          }
          break;
        }

        default: {
          return;
        }
      }
    } catch (err) {
      console.error(err); // eslint-disable-line
    }
  }, []);

  const { popoverVerticalAlignment, popoverHorizontalAlignment } = useDropdownPopoverAlignment(
    horizontalAlignment as HORIZONTAL_ALIGNMENT,
    verticalAlignment as VERTICAL_ALIGNMENT,
  );

  const OptionsListComponent = isVirtualized ? VirtualizedOptionsList : OptionsList;

  return (
    <SelectContainer
      data-test={props['data-test'] || `basic-select${id ? `-${id}` : ''}`}
      data-select-type="single"
      data-error={isError}
      id={id}
      ref={parentEl}
      className={className}
      style={style}
      isFullWidth={isFullWidth}
    >
      <Popover.Root
        open={isOpen}
        onOpenChange={(newOpen) => {
          if (!isNonInteractive) {
            toggleDropdown(newOpen);
          }
        }}
      >
        <Popover.Trigger asChild>
          <TriggerContainer ref={triggerContainerEl}>
            <SelectWrapper
              id={`trigger-${id}`}
              isError={isError}
              isNonInteractive={isNonInteractive}
              ref={parentEl}
              isFullWidth={isFullWidth}
              {...props}
              data-test={getBasicSelectWrapperTestId(id)}
            >
              <Trigger
                data-test="basic-select-value-display"
                isDisabled={isDisabled}
                isError={isError}
                isOpen={isOpen}
                isNonInteractive={isNonInteractive}
                isReadOnly={isReadOnly}
                placeholder={placeholder}
                ref={triggerEl}
                size={size}
                selectedOptions={selectedOptions}
                toggleDropdown={toggleDropdown}
                verticalAlignment={verticalAlignment}
                style={buttonStyles}
                suffixUsed={suffixUsed}
                isLoading={isLoading}
              />
            </SelectWrapper>
          </TriggerContainer>
        </Popover.Trigger>

        <Popover.Portal container={shouldContainPopover ? parentEl.current : undefined}>
          <Popover.Content
            asChild
            ref={portalElementRef}
            side={popoverVerticalAlignment}
            align={popoverHorizontalAlignment}
            avoidCollisions={automaticallyAvoidCollisions}
            hideWhenDetached={hideWhenDetached}
            data-portal-id={RAW_SELECT_DATA_PORTAL_ID}
            onInteractOutside={(e) => {
              // Prevent the popover from closing when clicking outside
              if (!allowClickOutsideToClose) {
                e.preventDefault();
              }
            }}
          >
            <SelectOptionsContainer
              {...props}
              {...ariaProps}
              isFullWidth={isFullWidth}
              verticalAlignment={verticalAlignment}
              horizontalAlignment={horizontalAlignment}
              $triggerWidth={parentEl?.current?.offsetWidth}
              data-test={`basic-select-option-container-${id}`}
            >
              <OptionWrapper
                as="div"
                onKeyDown={handleKeyPress}
                ref={optionsEl}
                role="listbox"
                isFullWidth={isFullWidth}
                {...ariaProps}
                {...props}
                data-test={`basic-select-option-wrapper-${id}`}
              >
                {children}
                <OptionsListComponent
                  Option={Option}
                  EmptyState={EmptyState}
                  canFetch={canFetch}
                  categoryDetail={categoryDetail}
                  categoryLabels={categoryLabels}
                  containerRef={optionsEl}
                  emptyDropdownDataTestId={SINGLE_SELECT_EMPTY_TEXT_TEST_ID}
                  emptyStateText={emptyStateText}
                  filterText={filterText}
                  getOptions={getOptions}
                  id={id}
                  isOpen={isOpen}
                  onFetch={onFetch}
                  singleSelect={singleSelect}
                  toggleDropdown={toggleDropdown}
                  isLoading={isLoading}
                  showCategoryDivider={showCategoryDivider}
                />
              </OptionWrapper>
              {footerButtonContent && <FooterButton>{footerButtonContent}</FooterButton>}
            </SelectOptionsContainer>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </SelectContainer>
  );
}
